import React, { useEffect, useMemo, useRef, useState } from "react";

import clsx from "clsx";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import { useFloating } from "@floating-ui/react";
import { FETCH_SAVED_SEARCHES } from "@gql/saved-searches";
import { useNorthbeamQuery } from "@hooks/http";
import { type FetchSavedSearches } from "@north-beam/nb-client-side";
import {
  SavedSearchMethod,
  SavedSearchOperator,
} from "@north-beam/nb-client-side";

import { useRecentSavedSearches } from "../hooks";
import { type SavedSearchTarget, type SearchQuery } from "../types";

import { ClearTextButton } from "./clear-text-button";
import { DebouncedInput } from "./debounced-input";
import { SaveSearchPopover } from "./save-search-popover";
import { SearchMenu } from "./search-menu";
import { SearchMethodCommaPopover } from "./search-method-comma-popover";
import { SearchMethodRegexPopover } from "./search-method-regex-popover";
import { SearchOperatorSelect } from "./search-operator-select";

interface Props {
  target: SavedSearchTarget;
  searchQuery: SearchQuery;
  showCampaignLabel: boolean;
  onChange(value: SearchQuery): void;
}

export const SearchInput = ({
  target,
  searchQuery,
  showCampaignLabel,
  onChange,
}: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();

  const { data, loading } = useNorthbeamQuery<FetchSavedSearches>(
    FETCH_SAVED_SEARCHES,
    { variables: { target } },
  );

  const inputRef = useRef<HTMLInputElement>(null);

  const [proposedSearchQuery, setProposedSearchQuery] = useState<SearchQuery>({
    method: SavedSearchMethod.text,
    operator: SavedSearchOperator.contains,
    query: "",
  });
  const [hasFocus, setFocus] = useState(false);
  const [isSearchOperatorSelectOpen, setIsSearchOperatorSelectOpen] =
    useState(false);
  const [isSaveSearchPopoverOpen, setIsSaveSearchPopoverOpen] = useState(false);
  const [isRegexPopoverOpen, setIsRegexPopoverOpen] = useState(false);
  const [isCommaPopoverOpen, setIsCommaPopoverOpen] = useState(false);

  const pageContext = {
    method: proposedSearchQuery.method,
    query: proposedSearchQuery.query,
    operator: proposedSearchQuery.operator,
  };

  useEffect(() => {
    setProposedSearchQuery({ ...searchQuery });
  }, [searchQuery]);

  const { recentSavedSearchIds, addSavedSearchIdToHistory } =
    useRecentSavedSearches(target);

  const { refs: searchMenuRefs, floatingStyles: searchMenuFloafingStyles } =
    useFloating({
      transform: false,
      placement: "bottom",
    });

  const isSaveButtonDisabled = useMemo(() => {
    if (proposedSearchQuery.query === "") {
      return true;
    }

    if (proposedSearchQuery.id && data?.me.savedSearches) {
      const savedSearch = data?.me.savedSearches.find(
        (e) => e.id === proposedSearchQuery.id,
      );

      if (savedSearch) {
        const isOperatorSame =
          proposedSearchQuery.operator === savedSearch.searchOperator;
        const isQuerySame = proposedSearchQuery.query === savedSearch.query;

        if (isOperatorSame && isQuerySame) {
          return true;
        }
      }
    }

    return false;
  }, [proposedSearchQuery, data]);

  return (
    <div
      ref={searchMenuRefs.setReference}
      className={clsx("relative flex items-center border rounded-md pl-2", {
        "border-cosmic+1": hasFocus,
        "border-gray hover:border-interstellar+2": !hasFocus,
        "border-interstellar+2":
          isSearchOperatorSelectOpen ||
          isSaveSearchPopoverOpen ||
          isRegexPopoverOpen ||
          isCommaPopoverOpen,
      })}
    >
      {loading ? (
        <div className="text-sm text-gray font-light ml-0.5 mr-1.5 h-[30px] flex items-center">
          Loading...
        </div>
      ) : (
        <>
          {proposedSearchQuery.method === SavedSearchMethod.text && (
            <>
              <i
                className={clsx(
                  "fa-regular fa-sharp fa-magnifying-glass",
                  "text-[0.75rem] text-gray-0 ml-0.5 mr-1.5",
                )}
              ></i>
              <DebouncedInput
                ref={inputRef}
                value={proposedSearchQuery.query}
                onChange={(query) => {
                  prettyLogEventPage({
                    domain: "Search Input",
                    action: "onChange",
                    additionalActionInfo: "Set search query",
                    data: {
                      newQuery: query,
                      pageContext,
                    },
                  });

                  onChange({ ...proposedSearchQuery, query });
                }}
                onFocus={() => setFocus(true)}
                onBlur={() => setFocus(false)}
              />
            </>
          )}
          {proposedSearchQuery.method === SavedSearchMethod.regex && (
            <SearchMethodRegexPopover
              isOpen={isRegexPopoverOpen}
              defaultValue={proposedSearchQuery.query}
              onApply={(val) => {
                prettyLogEventPage({
                  domain: "Search Input",
                  action: "onChange",
                  additionalActionInfo: "Set regex search",
                  data: {
                    newMethod: SavedSearchMethod.regex,
                    newQuery: val,
                    newOperator: null,
                    pageContext,
                  },
                });

                onChange({
                  method: SavedSearchMethod.regex,
                  operator: null,
                  query: val,
                });
                setIsRegexPopoverOpen(false);
              }}
              onCancel={() => {
                prettyLogEventPage({
                  domain: "Search Input",
                  action: "onChange",
                  additionalActionInfo: "Cancel regex search",
                  data: {
                    newMethod: SavedSearchMethod.text,
                    newOperator: SavedSearchOperator.contains,
                    newQuery: "",
                    pageContext,
                  },
                });

                if (proposedSearchQuery.query === "") {
                  const searchQuery = {
                    method: SavedSearchMethod.text,
                    operator: SavedSearchOperator.contains,
                    query: "",
                  };

                  setProposedSearchQuery(searchQuery);
                  onChange(searchQuery);
                }
                setIsRegexPopoverOpen(false);
              }}
              onOpenChange={(value) => {
                if (proposedSearchQuery.query === "") {
                  const searchQuery = {
                    method: SavedSearchMethod.text,
                    operator: SavedSearchOperator.contains,
                    query: "",
                  };

                  setProposedSearchQuery(searchQuery);
                  onChange(searchQuery);
                }
                setIsRegexPopoverOpen(value);
              }}
            />
          )}
          {proposedSearchQuery.method === SavedSearchMethod.comma && (
            <SearchMethodCommaPopover
              isOpen={isCommaPopoverOpen}
              defaultQuery={proposedSearchQuery.query}
              defaultOperator={proposedSearchQuery.operator!}
              onApply={(operator, query) => {
                prettyLogEventPage({
                  domain: "Search Input",
                  action: "onChange",
                  additionalActionInfo: "Set comma search",
                  data: {
                    newMethod: SavedSearchMethod.comma,
                    newOperator: operator,
                    newQuery: query,
                    pageContext,
                  },
                });

                onChange({
                  method: SavedSearchMethod.comma,
                  operator,
                  query,
                });
                setIsCommaPopoverOpen(false);
              }}
              onCancel={() => {
                prettyLogEventPage({
                  domain: "Search Input",
                  action: "onChange",
                  additionalActionInfo: "Cancel comma search",
                  data: {
                    newMethod: SavedSearchMethod.text,
                    newOperator: SavedSearchOperator.contains,
                    newQuery: "",
                    pageContext,
                  },
                });

                if (proposedSearchQuery.query === "") {
                  const searchQuery = {
                    method: SavedSearchMethod.text,
                    operator: SavedSearchOperator.contains,
                    query: "",
                  };

                  setProposedSearchQuery(searchQuery);
                  onChange(searchQuery);
                }
                setIsCommaPopoverOpen(false);
              }}
              onOpenChange={(value) => {
                if (proposedSearchQuery.query === "") {
                  const searchQuery = {
                    method: SavedSearchMethod.text,
                    operator: SavedSearchOperator.contains,
                    query: "",
                  };

                  setProposedSearchQuery(searchQuery);
                  onChange(searchQuery);
                }
                setIsCommaPopoverOpen(value);
              }}
            />
          )}
          {(hasFocus || proposedSearchQuery.query.length > 0) && (
            <>
              {proposedSearchQuery.query.length > 0 && (
                <>
                  <ClearTextButton
                    onClick={() => {
                      prettyLogEventPage({
                        domain: "Search Input",
                        action: "onClick",
                        additionalActionInfo: "Clear search query",
                        data: {
                          newMethod: SavedSearchMethod.text,
                          newOperator: SavedSearchOperator.contains,
                          newQuery: "",
                          pageContext,
                        },
                      });

                      onChange({
                        method: SavedSearchMethod.text,
                        operator: SavedSearchOperator.contains,
                        query: "",
                      });
                    }}
                  />
                  {proposedSearchQuery.method === SavedSearchMethod.text && (
                    <span className="h-4 border-r"></span>
                  )}
                </>
              )}
              {proposedSearchQuery.method === SavedSearchMethod.text && (
                <SearchOperatorSelect
                  isOpen={isSearchOperatorSelectOpen}
                  value={proposedSearchQuery.operator!}
                  showCampaignLabel={showCampaignLabel}
                  onChange={(operator) => {
                    prettyLogEventPage({
                      domain: "Search Input",
                      action: "onChange",
                      additionalActionInfo: "Set search operator",
                      data: {
                        newMethod: proposedSearchQuery.method,
                        newOperator: operator,
                        newQuery: proposedSearchQuery.query,
                        pageContext,
                      },
                    });

                    onChange({
                      ...proposedSearchQuery,
                      operator,
                    });
                  }}
                  onOpenChange={(value) => {
                    setIsSearchOperatorSelectOpen(value);
                  }}
                />
              )}
              <SaveSearchPopover
                target={target}
                isOpen={isSaveSearchPopoverOpen}
                disabled={isSaveButtonDisabled}
                searchQuery={proposedSearchQuery}
                savedSearches={data?.me.savedSearches || []}
                onOpenChange={(value) => {
                  setIsSaveSearchPopoverOpen(value);
                }}
                onSave={(value) => {
                  const saveParams = {
                    id: value.id,
                    method: value.searchMethod,
                    operator: value.searchOperator,
                    query: value.query,
                  };

                  prettyLogEventPage({
                    domain: "Search Input",
                    action: "onClick",
                    additionalActionInfo: "Save search",
                    data: {
                      pageContext: { ...saveParams },
                    },
                  });

                  onChange(saveParams);

                  setIsSaveSearchPopoverOpen(false);
                  addSavedSearchIdToHistory(value.id);
                }}
              />
            </>
          )}
          <SearchMenu
            refs={searchMenuRefs}
            target={target}
            floatingStyles={searchMenuFloafingStyles}
            isOpen={hasFocus && !isSearchOperatorSelectOpen}
            savedSearches={data?.me.savedSearches}
            recentSavedSearchIds={recentSavedSearchIds}
            onChangeSearchMethod={(value) => {
              prettyLogEventPage({
                domain: "Search Input",
                action: "onChange",
                additionalActionInfo: "Change search method",
                data: {
                  newMethod: value,
                  pageContext,
                },
              });

              if (proposedSearchQuery.method !== value) {
                const newQuery = { method: value, operator: null, query: "" };

                if (value === SavedSearchMethod.regex) {
                  setProposedSearchQuery(newQuery);
                } else {
                  setProposedSearchQuery({
                    ...newQuery,
                    operator: SavedSearchOperator.contains,
                  });
                }
              }

              if (value === SavedSearchMethod.regex) {
                setIsRegexPopoverOpen(true);
              }

              if (value === SavedSearchMethod.comma) {
                setIsCommaPopoverOpen(true);
              }
            }}
            onSelectSavedSearch={(value) => {
              const saveParams = {
                id: value.id,
                method: value.searchMethod,
                operator: value.searchOperator,
                query: value.query,
              };

              prettyLogEventPage({
                domain: "Search Input",
                action: "onChange",
                additionalActionInfo: "Select saved search",
                data: {
                  pageContext: { ...saveParams },
                },
              });

              onChange(saveParams);

              addSavedSearchIdToHistory(value.id);
              setFocus(false);
              inputRef.current?.blur();
            }}
            onDeleteSavedSearch={(value) => {
              if (value.id === proposedSearchQuery.id) {
                prettyLogEventPage({
                  domain: "Search Input",
                  action: "onChange",
                  additionalActionInfo: "Delete saved search",
                  data: {
                    pageContext: { id: value.id },
                  },
                });

                onChange({
                  method: SavedSearchMethod.text,
                  operator: SavedSearchOperator.contains,
                  query: "",
                });
              }
            }}
          />
        </>
      )}
    </div>
  );
};
